import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, Facebook } from "lucide-react";
import { siteContent } from "@/data/site-content";
import { showCookiePreferences } from "@/lib/cookie-consent";
import innsamlingsLogo from "@/assets/innsamlingskontrollen.png";
import logo from "@/assets/logo.png";

const footerLinks = [
  { label: "Hjem", href: "/" },
  { label: "Om oss", href: "/om-oss" },
  { label: "Tjenester", href: "/tjenester" },
  { label: "Kontakt", href: "/kontakt" },
  { label: "Nettbutikk", href: "/nettbutikk" },
];

const legalLinks = [
  { label: "Kjøpsvilkår", href: "/kjopsvilkar" },
  { label: "Personvern", href: "/personvern" },
  { label: "Vilkår fastgiver", href: "/vilkar-fastgiver" },
];

const Footer = () => {
  const { brand, globals } = siteContent;

  return (
    <footer className="border-t border-border bg-secondary/30">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-4">
          <div>
            <img src={logo} alt="Håp Naturlab" className="h-28 w-auto mb-4" width={112} height={112} />
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground font-body">
              {brand.tagline}
            </p>
          </div>

          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Sider
            </p>
            <ul className="mt-4 space-y-2">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Juridisk
            </p>
            <ul className="mt-4 space-y-2">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={showCookiePreferences}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground font-body text-left"
                >
                  Cookie-innstillinger
                </button>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Kontakt
            </p>
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground font-body">{globals.contact.name}</p>
              <a
                href={`mailto:${globals.contact.email}`}
                className="flex items-center gap-2 text-sm text-primary transition-colors hover:text-primary/80 font-body"
              >
                <Mail className="h-4 w-4" />
                {globals.contact.email}
              </a>
              {globals.contact.address && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground font-body">
                  <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{globals.contact.address}</span>
                </div>
              )}
              {globals.contact.openingHours && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span>{globals.contact.openingHours}</span>
                </div>
              )}
            </div>
            {globals.social?.facebook && (
              <a
                href={globals.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-primary transition-colors hover:text-primary/80 font-body"
              >
                <Facebook className="h-5 w-5" />
                Følg oss på Facebook
              </a>
            )}
            <div className="mt-6 flex items-center gap-3">
              <img src={innsamlingsLogo} alt="Godkjent av Innsamlingskontrollen" className="h-16 w-auto invert dark:invert-0" width={64} height={64} />
              <span className="text-xs text-muted-foreground font-body">Godkjent av<br />Innsamlingskontrollen</span>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-border pt-8 text-center">
          <p className="text-xs text-muted-foreground font-body">
            © {new Date().getFullYear()} {brand.name} · Org.nr: {globals.orgNr} · Alt overskudd går til syke barn.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
